import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MousePointer2 } from "lucide-react";
import { Reveal } from "./motion";
import { InViewTextEffect } from "@/components/ui/text-effect";
import { capacityStatus, nextSlotLabel } from "../../data/capacity";
import RunoffHeroPreview from "./RunoffHeroPreview";

const DURATION = 16000; // ms

// segments across the 16s loop. control: "you" (pine) | "me" (rust)
// Same six-zone mechanism for every site — only each zone's CONTENT is
// swapped per site (see SITES below), the timing/highlighting logic stays one.
const SEGMENTS = [
  { key: "photo", label: "Photo", start: 0, end: 18.75, control: "you", caption: "Your logo and photos — swap them anytime." },
  { key: "headline", label: "Headline", start: 18.75, end: 37.5, control: "you", caption: "Headline and body copy — edit whenever you want." },
  { key: "list", label: "Details", start: 37.5, end: 56.25, control: "you", caption: "Hours and service lists — always yours to update." },
  { key: "form", label: "Form", start: 56.25, end: 75, control: "me", caption: "The contact form — reverts to me." },
  { key: "nav", label: "Nav", start: 75, end: 87.5, control: "me", caption: "Navigation structure — reverts to me." },
  { key: "layout", label: "Layout", start: 87.5, end: 100, control: "me", caption: "Layout, hosting & security — reverts to me." },
];

const segAt = (pct) => SEGMENTS.find((s) => pct >= s.start && pct < s.end) || SEGMENTS[0];

// Per-site content for the mock browser. Bluebird's is the original, real
// content (untouched). Bedrock's real headline/sub/service-area/CTA are
// pulled from its actual live site (verified against it directly).
const SITES = {
  bluebird: {
    name: "Bluebird Day Painting Co.",
    url: "https://bluebird-day-painting.pages.dev",
    urlBar: "bluebird-day-painting.pages.dev",
    navLinks: ["Home", "Services", "Contact"],
    headline: "Paint that holds up at 6,000 feet.",
    sub: "Licensed & insured house painting across the Front Range, Colorado.",
    listLabel: "Hours",
    list: [["Mon–Fri", "7–5"], ["Sat", "8–12"], ["Sun", "Closed"]],
    formLabel: "Get a quote",
  },
  // Synced 2026-08-02 to the trust-first rebuild. The old entry still listed
  // "Fleet" and "Projects" nav links, which that rebuild removed.
  bedrock: {
    name: "Bedrock Earthworks",
    url: "https://bedrock-earthworks.pages.dev",
    urlBar: "bedrock-earthworks.pages.dev",
    navLinks: ["Services", "Quote"],
    headline: "We Move the Ground So You Can Build",
    sub: "Excavation, grading, and site prep across the Front Range — (719) 440-9494",
    listLabel: "Service Area",
    list: [["Front Range", "✓"], ["I-25 corridor", "✓"], ["To 9,000 ft", "✓"]],
    formLabel: "Request a Quote",
  },
  // Real headline/sub/hours pulled from its actual live site, same as Bedrock.
  runoff: {
    name: "Runoff Mobile Detail",
    url: "https://runoff-mobile-detail.pages.dev",
    urlBar: "runoff-mobile-detail.pages.dev",
    navLinks: ["Services", "How it works", "Service area"],
    headline: "Runoff clean. Right in your driveway.",
    sub: "We bring the water, the tools, and twelve years of doing this right — wherever the car actually is.",
    listLabel: "Hours",
    list: [["Mon–Fri", "8–6"], ["Sat", "9–3"], ["Sun", "Closed"]],
    formLabel: "Get a quote",
  },
};

// dashed annotation-style outline — deliberately NOT a solid glowing ring,
// so it doesn't read as a clickable/focused input or button
function zoneRing(active, control) {
  if (!active) return "border-2 border-dashed border-transparent";
  return control === "you"
    ? "border-2 border-dashed border-pine"
    : "border-2 border-dashed border-rust";
}

// small floating corner tag that makes it unambiguous this is an annotation,
// not an interactive element
function ZoneTag({ active, control }) {
  if (!active) return null;
  const isYou = control === "you";
  return (
    <span
      className={`pointer-events-none absolute -top-2.5 right-2 z-10 rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-paper ${
        isYou ? "bg-pine" : "bg-rust"
      }`}
    >
      {isYou ? "✎ Edit" : "🔒 Locked"}
    </span>
  );
}

export default function HandoffDemo() {
  const reduce = useReducedMotion();
  const [siteKey, setSiteKey] = useState("bluebird");
  const site = SITES[siteKey];
  const [pct, setPct] = useState(0); // 0..100
  const [playing, setPlaying] = useState(true);
  const [inView, setInView] = useState(true);
  const [hoveringLive, setHoveringLive] = useState(false);
  const dwellTimerRef = useRef(null);
  const DWELL_MS = 950;
  const rafRef = useRef(null);
  const lastRef = useRef(null);
  const pctRef = useRef(0);
  const sectionRef = useRef(null);

  const active = segAt(pct);
  const isZone = (key) => active.key === key;

  const switchSite = (key) => {
    if (key === siteKey) return;
    clearDwell();
    setSiteKey(key);
    pctRef.current = 0;
    setPct(0);
    setPlaying(true);
  };

  useEffect(() => {
    pctRef.current = pct;
  }, [pct]);

  // stop paying for this every frame once the section has scrolled out of view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const tick = useCallback((ts) => {
    if (lastRef.current == null) lastRef.current = ts;
    const dt = ts - lastRef.current;
    lastRef.current = ts;
    let next = pctRef.current + (dt / DURATION) * 100;
    if (next >= 100) next -= 100;
    pctRef.current = next;
    setPct(next);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (playing && !reduce && inView) {
      lastRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, reduce, inView, tick]);

  const jumpToSegment = (s) => {
    const mid = (s.start + s.end) / 2;
    pctRef.current = mid;
    setPct(mid);
  };

  const startDwell = () => {
    setHoveringLive(true);
    if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = setTimeout(() => {
      window.open(site.url, "_blank", "noopener,noreferrer");
    }, DWELL_MS);
  };
  const clearDwell = () => {
    setHoveringLive(false);
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  };
  useEffect(() => () => clearDwell(), []);

  return (
    <section
      ref={sectionRef}
      id="handoff"
      className="relative z-10 border-t border-hairline bg-panel/40 px-5 py-24 md:px-8 md:py-32"
      data-testid="handoff-section"
    >
      <div className="mx-auto max-w-6xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-[11px] uppercase tracking-[0.24em] text-rust"
        >
          CH.03 // Control Boundary
        </InViewTextEffect>
        <div className="mt-5 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal
            as="h2"
            className="max-w-xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl"
          >
            See exactly what <em className="italic text-pine">you</em> control —
            and what <em className="italic text-rust">reverts to me</em>.
          </Reveal>
          <Reveal delay={0.1} className="max-w-xs text-sm leading-relaxed text-ink-dim">
            No black box. Press play and watch the boundary map trace itself
            across a real, live build.
          </Reveal>
        </div>

        {/* site picker — switches the mock browser below between real builds */}
        <Reveal delay={0.08} className="mt-8 flex gap-2" data-testid="handoff-site-picker">
          {Object.entries(SITES).map(([key, s]) => (
            <button
              key={key}
              onClick={() => switchSite(key)}
              data-testid={`handoff-site-tab-${key}`}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                siteKey === key
                  ? "border-pine bg-pine text-paper"
                  : "border-hairline text-ink-dim hover:text-ink"
              }`}
            >
              {s.name}
            </button>
          ))}
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-start">
            {/* Browser mock */}
            <div>
            <div
              className={`overflow-hidden rounded-lg border border-hairline bg-paper transition-shadow duration-300 ${
                isZone("layout") ? "border-2 border-dashed border-rust" : ""
              }`}
              data-testid="mock-frame"
            >
              {/* chrome */}
              <div className="flex items-center gap-3 border-b border-hairline bg-panel px-4 py-3">
                <span className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
                </span>
                <span className="ml-2 flex-1 rounded-full border border-hairline bg-paper px-3 py-1 font-mono text-[10px] tracking-wide text-ink-dim">
                  {site.urlBar}
                </span>
              </div>

              <div className="p-5 md:p-6">
                {/* nav zone */}
                <div
                  className={`relative flex items-center justify-between rounded-md px-3 py-2 transition-all duration-300 ${zoneRing(
                    isZone("nav"),
                    "me"
                  )}`}
                  data-testid="mock-nav"
                >
                  <ZoneTag active={isZone("nav")} control="me" />
                  <span className="font-serif text-lg text-ink">{site.name}</span>
                  <span className="hidden gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-dim sm:flex">
                    {site.navLinks.map((l) => (
                      <span key={l}>{l}</span>
                    ))}
                  </span>
                </div>

                {/* photo zone — real photo (Bluebird) or brand-accurate graphic
                    (Bedrock, matching its real hazard-stripe hero treatment —
                    no static photo asset exists for it). Hover-dwell opens
                    whichever site is currently selected. */}
                <div
                  role="button"
                  tabIndex={0}
                  onMouseEnter={startDwell}
                  onMouseLeave={clearDwell}
                  onClick={() => window.open(site.url, "_blank", "noopener,noreferrer")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      window.open(site.url, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className={`group relative mt-4 block cursor-pointer overflow-hidden rounded-md transition-all duration-300 ${zoneRing(
                    isZone("photo"),
                    "you"
                  )}`}
                  data-testid="mock-photo"
                >
                  <ZoneTag active={isZone("photo")} control="you" />
                  {siteKey === "bluebird" ? (
                    <img
                      src="/demo-house.jpg"
                      alt="Bluebird Day Painting Co. — real project preview"
                      loading="lazy"
                      className="h-32 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 md:h-40"
                    />
                  ) : siteKey === "bedrock" ? (
                    <div className="relative h-32 w-full overflow-hidden md:h-40">
                      <div
                        className="absolute inset-x-0 top-0 z-10 h-2 w-full"
                        style={{
                          background:
                            "repeating-linear-gradient(-45deg, #F2B705, #F2B705 12px, #17160F 12px, #17160F 24px)",
                        }}
                      />
                      <img
                        src="/demo-excavator.jpg"
                        alt="Bedrock Earthworks — excavator on a Front Range grading site"
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                      />
                    </div>
                  ) : (
                    // No static photo asset for Runoff either — but unlike
                    // Bedrock, its real hero IS the interesting part, so embed
                    // the actual verified Hero component (ported as-is to
                    // RunoffHeroPreview.jsx) at natural size, scaled down —
                    // same technique this file already uses for the live
                    // hover-preview iframe below.
                    <div className="relative h-32 w-full overflow-hidden bg-[#071A24] transition-transform duration-700 ease-out group-hover:scale-110 md:h-40">
                      <div
                        className="pointer-events-none absolute left-0 top-0"
                        style={{ width: 1100, transform: "scale(0.42)", transformOrigin: "top left" }}
                      >
                        <RunoffHeroPreview />
                      </div>
                    </div>
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/0 font-mono text-[10px] uppercase tracking-[0.16em] text-paper opacity-0 transition-all duration-300 group-hover:bg-ink/55 group-hover:opacity-100">
                    Hold to step inside →
                  </span>

                  <AnimatePresence>
                    {hoveringLive && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.35, filter: "blur(6px)" }}
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        exit={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
                        transition={{ duration: DWELL_MS / 1000, ease: [0.16, 1, 0.3, 1] }}
                        style={{ transformOrigin: "top center" }}
                        className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-[320px] overflow-hidden rounded-lg border border-hairline bg-paper shadow-2xl md:block"
                      >
                        <iframe
                          src={site.url}
                          title={`${site.name} live preview`}
                          className="pointer-events-none border-0"
                          style={{
                            width: "1440px",
                            height: "900px",
                            transform: "scale(0.222)",
                            transformOrigin: "top left",
                          }}
                          loading="lazy"
                        />
                        <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-2 bg-ink/85 py-1.5 text-paper">
                          <svg width="12" height="12" viewBox="0 0 16 16" className="flex-shrink-0">
                            <circle cx="8" cy="8" r="6.5" fill="none" stroke="rgba(247,244,238,0.25)" strokeWidth="2" />
                            <motion.circle
                              cx="8" cy="8" r="6.5" fill="none" stroke="#F7F4EE" strokeWidth="2"
                              strokeDasharray={2 * Math.PI * 6.5}
                              initial={{ strokeDashoffset: 2 * Math.PI * 6.5 }}
                              animate={{ strokeDashoffset: 0 }}
                              transition={{ duration: DWELL_MS / 1000, ease: "linear" }}
                              transform="rotate(-90 8 8)"
                            />
                          </svg>
                          <span className="font-mono text-[9px] uppercase tracking-[0.14em]">
                            Release to stay · Hold to open
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <p className="mt-2 flex items-center justify-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-rust md:justify-start">
                  <MousePointer2 className="h-3 w-3 flex-none" />
                  Hover the photo above to preview the live site
                </p>

                {/* headline zone */}
                <div
                  className={`relative mt-4 rounded-md px-3 py-2 transition-all duration-300 ${zoneRing(
                    isZone("headline"),
                    "you"
                  )}`}
                  data-testid="mock-headline"
                >
                  <ZoneTag active={isZone("headline")} control="you" />
                  <h4 className="font-serif text-2xl leading-tight text-ink">
                    {site.headline}
                  </h4>
                  <p className="mt-1 text-xs leading-relaxed text-ink-dim">
                    {site.sub}
                  </p>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  {/* list zone (hours / service area, depending on site) */}
                  <div
                    className={`relative rounded-md px-3 py-2 transition-all duration-300 ${zoneRing(
                      isZone("list"),
                      "you"
                    )}`}
                    data-testid="mock-list"
                  >
                    <ZoneTag active={isZone("list")} control="you" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-dim">
                      {site.listLabel}
                    </p>
                    <ul className="mt-1.5 space-y-1 text-xs text-ink">
                      {site.list.map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>
                      ))}
                    </ul>
                  </div>

                  {/* form zone */}
                  <div
                    className={`relative rounded-md px-3 py-2 transition-all duration-300 ${zoneRing(
                      isZone("form"),
                      "me"
                    )}`}
                    data-testid="mock-form"
                  >
                    <ZoneTag active={isZone("form")} control="me" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-dim">
                      {site.formLabel}
                    </p>
                    <div className="mt-1.5 space-y-1.5">
                      <div className="h-5 rounded border border-hairline bg-panel/70" />
                      <div className="h-5 rounded border border-hairline bg-panel/70" />
                      <div className="h-6 rounded bg-pine" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-2 border-t border-hairline pt-4 sm:flex-row sm:items-center sm:justify-end">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-dim">
                Next slot:{" "}
                <span className={capacityStatus === "filled" ? "text-rust" : "text-pine"}>
                  {nextSlotLabel}
                </span>
              </span>
            </div>
            </div>

            {/* Controls + legend + caption */}
            <div className="flex flex-col gap-6">
              {/* caption */}
              <div className="min-h-[6rem] border border-hairline bg-paper p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">
                  {active.control === "you" ? "You control" : "Reverts to me"}
                </p>
                <div className="relative mt-2 h-12">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={active.key}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35 }}
                      className={`font-serif text-xl leading-snug ${
                        active.control === "you" ? "text-pine" : "text-rust"
                      }`}
                      data-testid="handoff-caption"
                    >
                      {active.caption}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>

              {/* legend */}
              <div className="flex gap-6" data-testid="handoff-legend">
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">
                  <span className="h-2.5 w-2.5 rounded-full bg-pine" /> You control
                </span>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">
                  <span className="h-2.5 w-2.5 rounded-full bg-rust" /> Reverts to me
                </span>
              </div>

              {/* scrubber — six real zone segments, click any to jump there */}
              <div className="border border-hairline bg-paper p-5">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setPlaying((p) => !p)}
                    data-testid="handoff-playpause"
                    aria-label={playing ? "Pause" : "Play"}
                    className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-ink text-paper transition-transform duration-200 hover:scale-105 active:scale-95"
                  >
                    {playing ? (
                      <span className="flex gap-1">
                        <span className="h-3.5 w-1 bg-paper" />
                        <span className="h-3.5 w-1 bg-paper" />
                      </span>
                    ) : (
                      <span className="ml-0.5 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-paper" />
                    )}
                  </button>

                  <div className="flex-1" data-testid="handoff-timeline">
                    <div className="flex gap-1.5">
                      {SEGMENTS.map((s, i) => {
                        const activeIdx = SEGMENTS.findIndex((x) => x.key === active.key);
                        const isYou = s.control === "you";
                        let fillPct = 0;
                        if (i < activeIdx) fillPct = 100;
                        else if (i === activeIdx) {
                          fillPct = Math.min(100, Math.max(0, ((pct - s.start) / (s.end - s.start)) * 100));
                        }
                        return (
                          <button
                            key={s.key}
                            onClick={() => jumpToSegment(s)}
                            data-testid={`handoff-seg-${s.key}`}
                            aria-label={`Jump to ${s.label}`}
                            title={s.label}
                            className="group relative h-1.5 flex-1 overflow-hidden rounded-full bg-ink/10"
                          >
                            <span
                              className={`absolute inset-y-0 left-0 rounded-full ${isYou ? "bg-pine" : "bg-rust"} ${
                                i === activeIdx ? "" : "transition-[width] duration-300"
                              }`}
                              style={{ width: `${fillPct}%` }}
                            />
                            <span className="absolute inset-0 rounded-full transition-colors duration-200 group-hover:bg-ink/10" />
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2.5 flex items-baseline justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink">
                        {active.label}
                      </span>
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.14em] ${
                          active.control === "you" ? "text-pine" : "text-rust"
                        }`}
                      >
                        {active.control === "you" ? "You control" : "Reverts to me"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
